import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { register, login } from '../../services/authService';

const RegisterContainer = styled.div`
 min-height: 100vh;
 background-color: white;
 font-family: 'Noto Sans Thai', sans-serif !important;
 * {
   font-family: 'Noto Sans Thai', sans-serif !important;
 }
`;

const Header = styled.div`
 background: white;
 padding: 16px 24px;
 display: flex;
 align-items: center;
 gap: 12px;
 box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Logo = styled.img`
 width: 32px;
 height: 32px;
 object-fit: contain;
`;

const HeaderTitle = styled.h1`
 font-size: 18px;
 font-weight: bold;
 color: #666;
 margin: 0;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StepIndicator = styled.div`
 display: flex;
 align-items: center;
 justify-content: center;
 padding: 24px;
 gap: 16px;
`;

const StepCircle = styled.div`
 width: 40px;
 height: 40px;
 border-radius: 50%;
 display: flex;
 align-items: center;
 justify-content: center;
 font-size: 18px;
 font-weight: 600;
 font-family: 'Noto Sans Thai', sans-serif !important;
 
 ${props => props.active ? `
   background-color: #00BBD3;
   color: white;
 ` : `
   background-color: #e1e5e9;
   color: #999;
 `}
`;

const StepLine = styled.div`
  width: 60px;
  height: 4px;
  background-color: ${props => props.active ? '#00BBD3' : '#e1e5e9'};
  border-radius: 2px;
  transition: background-color 0.3s ease;
`;
const Content = styled.div`
 padding: 24px;
 max-width: 400px;
 margin: 0 auto;
`;

const SectionTitle = styled.h2`
 font-size: 20px;
 font-weight: 600;
 color: #00BBD3;
 margin-bottom: 24px;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Section = styled.div`
 margin-bottom: 40px;
`;

const RadioGroup = styled.div`
 display: flex;
 align-items: center;
 gap: 12px;
 cursor: pointer;
 padding: 12px 16px;
 border-radius: 12px;
 transition: background-color 0.2s ease;
 min-height: 50px;
 margin-bottom: 16px;
 
 &:hover {
   background-color: #f0f9ff;
 }
`;

const RadioInput = styled.input.attrs({ type: 'radio' })`
 width: 24px;
 height: 24px;
 border: 3px solid #00BBD3;
 border-radius: 50%;
 background-color: white;
 cursor: pointer;
 appearance: none;
 position: relative;
 transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
 transform: scale(1);
 flex-shrink: 0;
 
 &:hover {
   transform: scale(1.05);
   border-color: #009bb3;
 }
 
 &:checked {
   background-color: #00BBD3;
   transform: scale(1.1);
 }
 
 &:checked::after {
   content: '';
   position: absolute;
   top: 50%;
   left: 50%;
   transform: translate(-50%, -50%);
   width: 10px;
   height: 10px;
   background-color: white;
   border-radius: 50%;
   animation: checkPulse 0.3s ease;
 }
 
 &:active {
   transform: scale(0.95);
 }
`;

const RadioLabel = styled.label`
 font-size: 18px;
 color: #666666;
 cursor: pointer;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
 line-height: 1.4;
 flex: 1;
`;

const SubSection = styled.div`
 margin-left: 40px;
 margin-top: 16px;
 max-height: ${props => props.show ? '400px' : '0'};
 overflow: hidden;
 opacity: ${props => props.show ? '1' : '0'};
 transform: translateY(${props => props.show ? '0' : '-10px'});
 transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const SubText = styled.p`
 font-size: 16px;
 color: #666666;
 margin-bottom: 16px;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const InputRow = styled.div`
 display: flex;
 align-items: center;
 gap: 12px;
 margin-bottom: 16px;
`;

const SmallInput = styled.input`
 width: 80px;
 padding: 12px 16px;
 border: 2px solid #e1e5e9;
 border-radius: 12px;
 font-size: 16px;
 color: #666666;
 background-color: white;
 transition: border-color 0.3s ease;
 font-family: 'Noto Sans Thai', sans-serif !important;
 font-weight: bold;
 text-align: center;

 &:focus {
   outline: none;
   border-color: #00BBD3;
 }
`;

const UnitLabel = styled.span`
 font-size: 16px;
 color: #666;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ButtonRow = styled.div`
 display: flex;
 gap: 16px;
 margin-top: 40px;
`;

const BackButton = styled.button`
 background-color: white;
 color: #00BBD3;
 border: 2px solid #00BBD3;
 border-radius: 30px;
 padding: 18px 32px;
 font-size: 18px;
 font-weight: bold;
 cursor: pointer;
 transition: all 0.3s ease;
 flex: 1;
 font-family: 'Noto Sans Thai', sans-serif !important;
 box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);

 &:hover {
   background-color: #00BBD3;
   color: white;
 }

 &:active {
   background-color: #009bb3;
 }
`;

const NextButton = styled.button`
 background-color: #00BBD3;
 color: white;
 border: none;
 border-radius: 30px;
 padding: 18px 32px;
 font-size: 18px;
 font-weight: bold;
 cursor: pointer;
 transition: all 0.3s ease;
 flex: 1;
 font-family: 'Noto Sans Thai', sans-serif !important;
 box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);

 &:hover {
   background-color: #009bb3;
 }

 &:active {
   background-color: #008aa0;
 }

 &:disabled {
   background-color: #cccccc;
   cursor: not-allowed;
 }
`;

const PrivacySection = styled.div`
 margin: 40px 0 32px 0;
 display: flex;
 align-items: flex-start;
 gap: 12px;
`;

const PrivacyCheckbox = styled.input.attrs({ type: 'checkbox' })`
 width: 20px;
 height: 20px;
 border: 2px solid #00BBD3;
 border-radius: 4px;
 background-color: white;
 cursor: pointer;
 flex-shrink: 0;
 margin-top: 2px;

 &:checked {
   background-color: #00BBD3;
   border-color: #00BBD3;
 }

 &:focus {
   outline: none;
   box-shadow: 0 0 0 3px rgba(0, 187, 211, 0.1);
 }
`;

const PrivacyLabel = styled.label`
 font-size: 16px;
 color: #666;
 line-height: 1.4;
 cursor: pointer;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PrivacyLink = styled.span`
 color: #00BBD3;
 text-decoration: underline;
 cursor: pointer;
 font-weight: bold;
 font-family: 'Noto Sans Thai', sans-serif !important;

 &:hover {
   color: #009bb3;
 }
`;

const CheckPulse = `
 @keyframes checkPulse {
   0% {
     transform: translate(-50%, -50%) scale(0);
     opacity: 0;
   }
   50% {
     transform: translate(-50%, -50%) scale(1.2);
     opacity: 0.8;
   }
   100% {
     transform: translate(-50%, -50%) scale(1);
     opacity: 1;
   }
 }
`;

const GlobalStyles = `
 ${CheckPulse}
`;

const RegisterStep2 = () => {
 const navigate = useNavigate();
 const location = useLocation();

 // ดึงข้อมูลจาก location.state หรือ localStorage
 const getPreviousData = () => {

   if (location.state && location.state.step1Data) {
     return location.state;
   }

   // ถ้าไม่มีใน location.state ให้ดึงจาก localStorage
   const savedData = localStorage.getItem('registerStep1Data');
   const savedDiseaseData = localStorage.getItem('registerStep1DiseaseData');

   if (savedData) {
     try {
       const parsedStep1 = JSON.parse(savedData);
       const parsedDiseaseData = savedDiseaseData ? JSON.parse(savedDiseaseData) : null;
       return {
         step1Data: parsedStep1,
         diseaseData: parsedDiseaseData
       };
     } catch (error) {
       return {};
     }
   }

   // ถ้าไม่มีข้อมูลเลย ให้กลับไป step1
   setTimeout(() => {
     alert('ไม่พบข้อมูลจากขั้นตอนก่อนหน้า กรุณากรอกข้อมูลใหม่');
     window.location.href = '/register/step1';
   }, 100);
   return {};
 };

 const previousData = getPreviousData();

 const [alcoholData, setAlcoholData] = useState({
   status: '',
   amount: '',
   frequency: ''
 });

 const [smokingData, setSmokingData] = useState({
   status: '',
   amount: '',
   duration: '',
   quitAmount: ''
 });

 const [isPrivacyAccepted, setIsPrivacyAccepted] = useState(false);
 const [loading, setLoading] = useState(false);

 const handleAlcoholChange = (field, value) => {
   setAlcoholData(prev => ({
     ...prev,
     [field]: value
   }));
 };

 const handleSmokingChange = (field, value) => {
   setSmokingData(prev => ({
     ...prev,
     [field]: value
   }));
 };

 const handleAlcoholRadioClick = (value) => {
   handleAlcoholChange('status', value);
 };

 const handleSmokingRadioClick = (value) => {
   handleSmokingChange('status', value);
 };

 const handleBack = () => {
   navigate('/register/step1');
 };

 const handleNext = async () => {
   if (!alcoholData.status || !smokingData.status) {
     alert('กรุณาเลือกข้อมูลการดื่มแอลกอฮอล์และการสูบบุหรี่');
     return;
   }

   if (alcoholData.status === 'ดื่มเป็นประจำ' && (!alcoholData.amount || !alcoholData.frequency)) {
     alert('กรุณากรอกข้อมูลการดื่มแอลกอฮอล์ให้ครบถ้วน');
     return;
   }

   if (smokingData.status === 'สูบบุหรี่' && (!smokingData.amount || !smokingData.duration)) {
     alert('กรุณากรอกข้อมูลการสูบบุหรี่ให้ครบถ้วน');
     return;
   }

   if (smokingData.status === 'หยุดสูบบุหรี่' && (!smokingData.amount || !smokingData.duration || !smokingData.quitAmount)) {
     alert('กรุณากรอกข้อมูลการหยุดสูบบุหรี่ให้ครบถ้วน');
     return;
   }

   if (!isPrivacyAccepted) {
     alert('กรุณาอ่านและยอมรับ ประกาศนโยบายความเป็นส่วนตัว');
     return;
   }

   setLoading(true);

   try {
     const { step1Data, diseaseData } = previousData;

     // Combine birthDay, birthMonth, birthYear into birthdate format
     const birthdate = `${step1Data.birthYear}-${step1Data.birthMonth.toString().padStart(2, '0')}-${step1Data.birthDay.toString().padStart(2, '0')}`;

     // Convert diseaseData.diseases object to congenital_disease array
     const congenitalDiseaseList = [];
     if (diseaseData && diseaseData.diseases) {
       const diseaseMap = {
         diabetes: 'เบาหวาน',
         hypertension: 'ความดันโลหิตสูง',
         kidneyDisease: 'โรคไต',
         hyperlipidemia: 'ไขมันในเลือดสูง',
         heartDisease: 'โรคหัวใจ',
         stroke: 'โรคหลอดเลือดสมอง',
         thyroid: 'ต่อมไทรอยด์',
         emphysema: 'ถุงลมโป่งพอง'
       };

       for (const [key, value] of Object.entries(diseaseData.diseases)) {
         if (value === true && diseaseMap[key]) {
           congenitalDiseaseList.push(diseaseMap[key]);
         }
       }
     }

     const registerData = {
       idCard: step1Data.idCard,         // ID card number (for login username)
       phone: step1Data.phone,            // Phone number
       firstname: step1Data.firstName,    // Note: firstName with capital N from step1
       lastname: step1Data.lastName,      // Note: lastName with capital N from step1
       gender: step1Data.gender,
       birthdate: birthdate,
       height: step1Data.height,
       weight: step1Data.weight,
       waist: step1Data.waist,            // Waistline
       congenital_disease: congenitalDiseaseList,
       alcohol: alcoholData.status,
       alcohol_amount: alcoholData.amount || '',
       alcohol_frequency: alcoholData.frequency || '',
       smoking: smokingData.status,
       smoking_amount: smokingData.amount || '',
       smoking_duration: smokingData.duration || '',
       smoking_quit_amount: smokingData.quitAmount || ''
     };

     const response = await register(registerData);

     // Auto-login after successful registration
     const loginResponse = await login(step1Data.idCard, birthdate);

     if (loginResponse.result && loginResponse.token) {
       // Login successful, navigate to dashboard
       navigate('/dashboard');
     } else {
       // Login failed, navigate to login page
       alert('ลงทะเบียนสำเร็จ กรุณาเข้าสู่ระบบ');
       navigate('/');
     }
   } catch (error) {

     let errorMessage = 'เกิดข้อผิดพลาดในการลงทะเบียน กรุณาลองใหม่อีกครั้ง';

     if (error.response?.data?.message) {
       errorMessage = error.response.data.message;

       // แปลข้อความ error ที่เป็นภาษาอังกฤษ
       if (errorMessage.includes('already exists') || errorMessage.includes('มีผู้ใช้นี้แล้ว')) {
         errorMessage = 'เบอร์โทรศัพท์นี้ถูกใช้งานแล้ว กรุณาใช้เบอร์อื่น';
       }
     }

     alert(errorMessage);
   } finally {
     setLoading(false);
   }
 };

 return (
   <>
     <style>{GlobalStyles}</style>
     <RegisterContainer>
       <Header>
         <Logo src="/logo.png" alt="MFU Logo" />
         <HeaderTitle>MFU Wellness Center</HeaderTitle>
       </Header>

       <StepIndicator>
        <StepCircle active>1</StepCircle>
        <StepLine active />
        <StepCircle active>2</StepCircle>
        </StepIndicator>

       <Content>
         <Section>
           <SectionTitle>การดื่มแอลกอฮอล์</SectionTitle>
           
           <RadioGroup onClick={() => handleAlcoholRadioClick('ไม่เคยดื่ม')}>
             <RadioInput
               name="alcohol"
               value="ไม่เคยดื่ม"
               checked={alcoholData.status === 'ไม่เคยดื่ม'}
               onChange={(e) => handleAlcoholChange('status', e.target.value)}
             />
             <RadioLabel>ไม่เคยดื่ม</RadioLabel>
           </RadioGroup>

           <RadioGroup onClick={() => handleAlcoholRadioClick('ดื่มเป็นบางโอกาส')}>
             <RadioInput
               name="alcohol"
               value="ดื่มเป็นบางโอกาส"
               checked={alcoholData.status === 'ดื่มเป็นบางโอกาส'}
               onChange={(e) => handleAlcoholChange('status', e.target.value)}
             />
             <RadioLabel>ดื่มเป็นบางโอกาส</RadioLabel>
           </RadioGroup>

           <RadioGroup onClick={() => handleAlcoholRadioClick('ดื่มเป็นประจำ')}>
             <RadioInput
               name="alcohol"
               value="ดื่มเป็นประจำ"
               checked={alcoholData.status === 'ดื่มเป็นประจำ'}
               onChange={(e) => handleAlcoholChange('status', e.target.value)}
             />
             <RadioLabel>ดื่มเป็นประจำ</RadioLabel>
           </RadioGroup>

           <SubSection show={alcoholData.status === 'ดื่มเป็นประจำ'}>
             <SubText>คุณเริ่มดื่มแอลกอฮอล์เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={alcoholData.amount}
                 onChange={(e) => handleAlcoholChange('amount', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>

             <SubText>โดยเฉลี่ยปัจจุบันดื่มแอลกอฮอล์</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={alcoholData.frequency}
                 onChange={(e) => handleAlcoholChange('frequency', e.target.value)}
               />
               <UnitLabel>ขวด/วัน</UnitLabel>
             </InputRow>
           </SubSection>

           <RadioGroup onClick={() => handleAlcoholRadioClick('หยุดดื่ม')}>
             <RadioInput
               name="alcohol"
               value="หยุดดื่ม"
               checked={alcoholData.status === 'หยุดดื่ม'}
               onChange={(e) => handleAlcoholChange('status', e.target.value)}
             />
             <RadioLabel>หยุดดื่ม</RadioLabel>
           </RadioGroup>

           <SubSection show={alcoholData.status === 'หยุดดื่ม'}>
             <SubText>คุณเริ่มดื่มแอลกอฮอล์เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={alcoholData.amount}
                 onChange={(e) => handleAlcoholChange('amount', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>

             <SubText>โดยเฉลี่ยปัจจุบันดื่มแอลกอฮอล์</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={alcoholData.frequency}
                 onChange={(e) => handleAlcoholChange('frequency', e.target.value)}
               />
               <UnitLabel>ขวด/วัน</UnitLabel>
             </InputRow>

             <SubText>คุณหยุดดื่มแอลกอฮอล์เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={alcoholData.quitAge}
                 onChange={(e) => handleAlcoholChange('quitAge', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>
           </SubSection>
         </Section>

         <Section>
           <SectionTitle>การสูบบุหรี่</SectionTitle>
           
           <RadioGroup onClick={() => handleSmokingRadioClick('ไม่เคยสูบ')}>
             <RadioInput
               name="smoking"
               value="ไม่เคยสูบ"
               checked={smokingData.status === 'ไม่เคยสูบ'}
               onChange={(e) => handleSmokingChange('status', e.target.value)}
             />
             <RadioLabel>ไม่เคยสูบ</RadioLabel>
           </RadioGroup>

           <RadioGroup onClick={() => handleSmokingRadioClick('สูบบุหรี่')}>
             <RadioInput
               name="smoking"
               value="สูบบุหรี่"
               checked={smokingData.status === 'สูบบุหรี่'}
               onChange={(e) => handleSmokingChange('status', e.target.value)}
             />
             <RadioLabel>สูบบุหรี่</RadioLabel>
           </RadioGroup>

           <SubSection show={smokingData.status === 'สูบบุหรี่'}>
             <SubText>คุณเริ่มสูบบุหรี่เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={smokingData.amount}
                 onChange={(e) => handleSmokingChange('amount', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>

             <SubText>โดยเฉลี่ยปัจจุบันคุณสูบบุหรี่</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={smokingData.duration}
                 onChange={(e) => handleSmokingChange('duration', e.target.value)}
               />
               <UnitLabel>มวน/วัน</UnitLabel>
             </InputRow>
           </SubSection>

           <RadioGroup onClick={() => handleSmokingRadioClick('หยุดสูบบุหรี่')}>
             <RadioInput
               name="smoking"
               value="หยุดสูบบุหรี่"
               checked={smokingData.status === 'หยุดสูบบุหรี่'}
               onChange={(e) => handleSmokingChange('status', e.target.value)}
             />
             <RadioLabel>หยุดสูบบุหรี่</RadioLabel>
           </RadioGroup>

           <SubSection show={smokingData.status === 'หยุดสูบบุหรี่'}>
             <SubText>คุณเริ่มสูบบุหรี่เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={smokingData.amount}
                 onChange={(e) => handleSmokingChange('amount', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>

             <SubText>คุณหยุดสูบบุหรี่เมื่ออายุ</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={smokingData.duration}
                 onChange={(e) => handleSmokingChange('duration', e.target.value)}
               />
               <UnitLabel>ปี</UnitLabel>
             </InputRow>

             <SubText>โดยเฉลี่ยปัจจุบันคุณสูบบุหรี่</SubText>
             <InputRow>
               <SmallInput
                 type="number"
                 value={smokingData.quitAmount}
                 onChange={(e) => handleSmokingChange('quitAmount', e.target.value)}
               />
               <UnitLabel>มวน/วัน</UnitLabel>
             </InputRow>
           </SubSection>
         </Section>

         <PrivacySection>
           <PrivacyCheckbox
             id="privacy-policy"
             checked={isPrivacyAccepted}
             onChange={(e) => setIsPrivacyAccepted(e.target.checked)}
           />
           <PrivacyLabel htmlFor="privacy-policy">
             คุณได้อ่านและยอมรับ ประกาศนโยบายความเป็นส่วนตัว{' '}
             <PrivacyLink onClick={() => window.open('https://dietz.asia/terms.html', '_blank')}>
               (Privacy Notice)
             </PrivacyLink>
           </PrivacyLabel>
         </PrivacySection>

         <ButtonRow>
           <BackButton onClick={handleBack} disabled={loading}>
             ก่อนหน้า
           </BackButton>
           <NextButton onClick={handleNext} disabled={loading}>
             {loading ? 'กำลังลงทะเบียน...' : 'ลงทะเบียน'}
           </NextButton>
         </ButtonRow>
       </Content>
     </RegisterContainer>
   </>
 );
};

export default RegisterStep2;